import React, { useState, useEffect } from "react";
import { View, ScrollView, Platform, Linking } from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { Feather } from "@expo/vector-icons";
import { AppText } from "@/components/ui/AppText";
import { AppTextInput } from "@/components/ui/TextInput";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { Button } from "@/components/ui/Button";
import { ErrorState } from "@/components/ui/ErrorState";
import { FileUpload, type SelectedFile, type FileUploadState } from "@/components/ui/FileUpload";
import { Icon } from "@/components/ui/Icon";
import { Modal } from "@/components/ui/Modal";
import { PageFooter } from "@/components/ui/PageFooter";
import { SuccessState } from "@/components/ui/SuccessState";
import { useI18n } from "@/context/I18nContext";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import { cn } from "@/lib/utils";
import { vendorStageLabels } from "@/constants";
import type { ApplicationStageId } from "@/lib/api/vendor";
import { preVettingApi } from "@/lib/api/preVetting";
import { useApplicationDetail } from "./useApplicationDetail";
import { FIELD_MAP } from "./fieldMap";

// ─── Types ────────────────────────────────────────────────────────────────────

type ModalStep = "confirm" | "success" | "error";

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function UpdateSubmissionScreen() {
  const { t } = useI18n();
  const router = useRouter();
  const isWide = useIsWideScreen();

  const { stage, fieldKey } = useLocalSearchParams<{
    stage: string;
    fieldKey: string;
  }>();

  const stageId = stage as ApplicationStageId;
  const stageLabel = vendorStageLabels[stageId];

  const { data: detail } = useApplicationDetail();

  // Redirect away if the application is no longer awaiting changes.
  // This guards against direct deep-link access when status is approved/rejected.
  const applicationStatus = detail?.status;
  useEffect(() => {
    if (applicationStatus && applicationStatus !== "pending_review") {
      router.replace({
        pathname: "/(vendor)/application/[stage]",
        params: { stage: stageId },
      });
    }
  }, [applicationStatus, router, stageId]);

  const isEditAllowed = !applicationStatus || applicationStatus === "pending_review";

  const fieldEntry = fieldKey
    ? (FIELD_MAP[fieldKey] ?? FIELD_MAP[fieldKey.replace(/^step\d+\./, "")])
    : undefined;
  const isUpload = fieldEntry?.type === "upload";
  const fieldLabel = fieldEntry ? t(fieldEntry.labelKey) : (fieldKey ?? "");
  const currentValue = (detail && fieldEntry ? fieldEntry.getValue(detail) : null) ?? "";
  const currentFileName =
    (detail && fieldEntry?.getFileName ? fieldEntry.getFileName(detail) : null) ?? currentValue;
  const reviewerNote = detail?.fieldRejections?.[fieldKey]?.reason;

  // ── Text state ──────────────────────────────────────────────────────────────
  const [correction, setCorrection] = useState("");

  // ── Upload state ────────────────────────────────────────────────────────────
  const [uploadedFile, setUploadedFile] = useState<SelectedFile | null>(null);
  const [uploadState, setUploadState] = useState<FileUploadState>("idle");
  const [uploadedKey, setUploadedKey] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState(false);

  const [modalStep, setModalStep] = useState<ModalStep | null>(null);

  const canSubmit = isEditAllowed && (isUpload ? uploadedKey !== null : correction.trim().length > 0);

  const handleFileSelected = async (file: SelectedFile) => {
    setUploadedFile(file);
    setUploadState("uploading");
    setUploadError(false);
    try {
      const key = await preVettingApi.uploadDocument(file);
      setUploadedKey(key);
      setUploadState("success");
    } catch {
      setUploadState("error");
      setUploadError(true);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setUploadState("idle");
    setUploadedKey(null);
    setUploadError(false);
  };

  const handleSubmitPress = () => {
    setModalStep("confirm");
  };

  const handleConfirm = async () => {
    if (!isEditAllowed) {
      setModalStep(null);
      return;
    }
    try {
      const correctedValue = isUpload ? (uploadedKey ?? "") : correction.trim();

      if (fieldEntry) {
        const currentDraft = await preVettingApi.getDraft();
        const updatedDraft = fieldEntry.patchDraft(currentDraft, correctedValue);
        await preVettingApi.saveDraft(updatedDraft);
      }

      await preVettingApi.submit();
      setModalStep("success");
    } catch {
      setModalStep("error");
    }
  };

  const handleDismiss = () => {
    if (modalStep === "success") {
      router.navigate({
        pathname: "/(vendor)/application/[stage]",
        params: { stage: stageId, updatedField: fieldKey },
      });
      setModalStep(null);
    } else {
      setModalStep(null);
    }
  };

  return (
    <View className="flex-1 bg-stroke-10">
      {/* ── Desktop breadcrumb band ───────────────────────── */}
      <View
        className={cn(
          "px-page-x bg-white py-4 border-b border-stroke-50",
          Platform.select({ web: "hidden tablet:flex", native: "hidden" })
        )}
      >
        <Breadcrumb
          segments={[
            {
              labelKey: "vendor.dashboard.nav.application",
              href: "/(vendor)/(dashboard)/application",
            },
            {
              labelText: stageLabel ? t(stageLabel.title) : t("vendor.dashboard.nav.application"),
              href: `/(vendor)/application/${stageId}`,
            },
            { labelKey: "vendor.application.update.breadcrumb" },
          ]}
        />
      </View>

      {/* ── Desktop title + action bar ────────────────────── */}
      {isWide && Platform.OS === "web" && (
        <View className="bg-white px-20 pt-5 pb-6 flex-row items-start justify-between">
          <View className="flex-1 mr-6">
            <AppText
              weight="bold"
              className="text-[29px] leading-[32px] tracking-[-0.29px] text-dark-text-50"
            >
              {fieldLabel}
            </AppText>
            {stageLabel && (
              <AppText weight="medium" className="text-base text-supporting-text-40 mt-1">
                {t(stageLabel.title)}
              </AppText>
            )}
          </View>
          <Button
            variant="primary"
            label="vendor.application.update.submitUpdate"
            onPress={handleSubmitPress}
            disabled={!canSubmit}
          />
        </View>
      )}

      <ScrollView className="flex-1" contentContainerStyle={{ flexGrow: 1 }}>
        {/* ── Mobile title ──────────────────────────── */}
        {!isWide && (
          <View className="px-page-x py-4 bg-white border-b border-stroke-50">
            <AppText weight="bold" className="text-xl text-dark-text-50">
              {fieldLabel}
            </AppText>
            {stageLabel && (
              <AppText weight="medium" className="text-sm text-supporting-text-40 mt-0.5">
                {t(stageLabel.title)}
              </AppText>
            )}
          </View>
        )}

        <View
          className={cn("gap-4 max-w-2xl self-center w-full", isWide ? "px-10 py-6" : "px-5 py-4")}
        >
          {/* ── Reviewer Note ─────────────────────────── */}
          {!!reviewerNote && (
            <View className="bg-orange-light border border-orange-10 rounded-lg p-4 gap-1">
              <AppText weight="bold" className="text-xs text-orange-60 uppercase tracking-widest">
                {t("vendor.application.update.reviewerNote")}
              </AppText>
              <AppText weight="medium" className="text-sm text-supporting-text mt-1">
                {reviewerNote}
              </AppText>
            </View>
          )}

          {/* ── Update Submission card ─────────────────── */}
          <View className="bg-white rounded-xl border border-stroke-50 overflow-hidden">
            <View className="px-6 pt-5 pb-1">
              <AppText weight="semibold" className="text-base text-dark-text-50">
                {t("vendor.application.update.sectionTitle")}
              </AppText>
            </View>
            <View className="px-6 pb-6 pt-3 gap-4">
              <AppTextInput
                label="vendor.application.update.yourSubmission"
                value={isUpload ? currentFileName : currentValue}
                editable={false}
              />
              {isUpload ? (
                <FileUpload
                  label="vendor.application.update.newDocument"
                  onFile={handleFileSelected}
                  uploadState={uploadState}
                  currentFile={uploadedFile}
                  onRemoveFile={handleRemoveFile}
                  onViewFile={
                    uploadedFile && uploadState === "success"
                      ? () => Linking.openURL(uploadedFile.uri)
                      : undefined
                  }
                  error={uploadError ? "fileUpload.uploadFailed" : undefined}
                  accept={["application/pdf", "image/png", "image/jpeg"]}
                  maxSizeMB={2}
                />
              ) : (
                <AppTextInput
                  label="vendor.application.update.correction"
                  placeholder="vendor.application.update.correctionPlaceholder"
                  value={correction}
                  onChangeText={setCorrection}
                />
              )}
            </View>
          </View>
        </View>

        <PageFooter />
      </ScrollView>

      {/* ── Mobile sticky submit button ───────────────────── */}
      {!isWide && (
        <View className="px-5 pb-8 pt-3 bg-white border-t border-stroke-50">
          <Button
            variant="primary"
            label="vendor.application.update.submitUpdate"
            onPress={handleSubmitPress}
            disabled={!canSubmit}
            className="w-full"
          />
        </View>
      )}

      {/* ── Confirmation / Success / Error modal ─────────── */}
      <Modal
        visible={modalStep !== null}
        onDismiss={handleDismiss}
        title={modalStep === "confirm" ? "vendor.application.update.confirmTitle" : undefined}
        footer={
          modalStep === "confirm" ? (
            <View className="flex-row gap-3 justify-end">
              <Button label="common.cancel" variant="tertiary" onPress={handleDismiss} />
              <Button
                label="vendor.application.update.yesSubmit"
                variant="primary"
                onPress={handleConfirm}
              />
            </View>
          ) : undefined
        }
      >
        {modalStep === "confirm" && (
          <View className="gap-4">
            <AppText className="text-sm text-supporting-text">
              {t("vendor.application.update.confirmBody")}
            </AppText>
            <View className="bg-stroke-10 rounded-lg p-4 gap-2">
              <AppText
                weight="medium"
                className="text-xs text-supporting-text uppercase tracking-wide"
              >
                {fieldLabel}
              </AppText>
              {isUpload ? (
                <View className="bg-white border border-stroke-50 rounded-md px-3 py-2.5 flex-row items-center gap-2">
                  <Icon as={Feather} name="file-text" size={16} className="text-supporting-text" />
                  <AppText weight="medium" className="text-sm text-dark-text-50" numberOfLines={1}>
                    {uploadedFile?.name ?? ""}
                  </AppText>
                </View>
              ) : (
                <AppText weight="semibold" className="text-base text-dark-text-50">
                  {correction}
                </AppText>
              )}
            </View>
          </View>
        )}
        {modalStep === "success" && (
          <SuccessState
            title="vendor.application.update.successTitle"
            description="vendor.application.update.successDescription"
          />
        )}
        {modalStep === "error" && <ErrorState onRetry={handleConfirm} />}
      </Modal>
    </View>
  );
}
